
export const REACTION_EMOJI: Record<string, string> = {
  like: "\uD83D\uDC4D",
  love: "\u2764\uFE0F",
  haha: "\uD83D\uDE02",
  wow: "\uD83D\uDE2E",
  sad: "\uD83D\uDE22",
  angry: "\uD83D\uDE21",
};

/** How many reactor names a "who reacted" tooltip lists before it says "og N andre". */
const REACTION_TOOLTIP_MAX_NAMES = 10;

interface ReactionTooltipContent<E> {
  /** One line per emoji that actually has named reactors, biggest group first. */
  lines: { entry: E; names: string[] }[];
  /** Names that didn't fit in the budget — 0 when everything is listed. */
  overflow: number;
}

/**
 * The body of a "who reacted" tooltip: reactor names grouped under their emoji.
 *
 * Shared by components/ReactionSummary.tsx (posts/comments) and
 * app/beskeder/DmReactionSummary.tsx (DMs). The two surfaces read from
 * different tables behind different endpoints, but the tooltip they build from
 * the result is the same algorithm, and had drifted apart once already — so it
 * lives here rather than in either component.
 *
 * `entries` is generic because the count rows differ by surface
 * (`EmojiReactionCount` vs `MessageReaction`); only `emoji` and `count` are
 * read, and the caller gets its own row back on each line to render from.
 *
 * `reactions` is structural for the same reason, and because the two surfaces
 * are mid-migration: a v1 `Reactor` names the person under `user`, the legacy
 * `Reaction` carries `name`/`username` inline. Ruling O-21 took `username` off
 * the wire, so the `user.name` arm is the one that survives — drop the other
 * two when components/ReactionSummary.tsx moves to `GET /posts/{id}/reactions`.
 */
interface ReactorLike {
  emoji: string;
  user?: { name: string | null } | null;
  name?: string | null;
  username?: string;
}

export function buildReactionTooltip<E extends { emoji: string; count: number }>(
  entries: E[],
  reactions: ReactorLike[],
  maxNames: number = REACTION_TOOLTIP_MAX_NAMES
): ReactionTooltipContent<E> {
  const grouped = [...entries]
    .sort((a, b) => b.count - a.count)
    .map((entry) => ({
      entry,
      names: reactions
        .filter((r) => r.emoji === entry.emoji)
        .map((r) => r.user?.name || r.name || r.username || "Ukendt"),
    }))
    .filter((g) => g.names.length > 0);

  const totalNames = grouped.reduce((sum, g) => sum + g.names.length, 0);
  const overflow = Math.max(0, totalNames - maxNames);

  // Fill up to maxNames across the groups in order, dropping the rest.
  // A plain loop rather than a mutating .map(): the budget only ever grows,
  // so once it's spent every later group is dropped anyway — and this drops
  // the `as` cast that was standing in for TS narrowing past .filter(Boolean).
  const lines: { entry: E; names: string[] }[] = [];
  let shownSoFar = 0;
  for (const g of grouped) {
    const remaining = maxNames - shownSoFar;
    if (remaining <= 0) break;
    const sliced = g.names.slice(0, remaining);
    shownSoFar += sliced.length;
    lines.push({ entry: g.entry, names: sliced });
  }

  return { lines, overflow };
}
